import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useMDCockpit } from '../../../../hooks/useMDCockpit';
import { useCesium } from '../../../../context/CesiumContext';
import TargetBody, { geodeticToGeocentric, geocentricToGeodetic, geodeticToEcef, ecefToGeodetic } from './TargetBody';
import PolygonBody from './PolygonBody';
import type { GeoFeature } from '../../../../types/mission';
import { createGeoFeature, updateGeoFeatureApi } from '../../../../service/geoFeatureApi';
import { showToastMessage } from '../../../../../../../utils/commonFunctions';
import styles from './GeoFeaturePanel.module.scss';

type Tool = 'target' | 'polygon';

const PointTabIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="4.69882" stroke="currentColor" strokeWidth="0.602353" />
    <circle cx="8" cy="8" r="1" fill="currentColor" />
  </svg>
);

const PolygonTabIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M5.55753 5.70877C5.45903 5.23699 5.04077 4.8826 4.53974 4.8826C3.96551 4.8826 3.5 5.34811 3.5 5.92235C3.5 6.49658 3.96551 6.96209 4.53974 6.96209C5.11398 6.96209 5.57949 6.49658 5.57949 5.92235C5.57949 5.84914 5.57192 5.7777 5.55753 5.70877ZM4.53974 6.96209V10.4194M9.06028 11.4592C9.06028 12.0334 9.52579 12.4989 10.1 12.4989C10.6743 12.4989 11.1398 12.0334 11.1398 11.4592C11.1398 10.9481 10.7711 10.5232 10.2852 10.4359M9.06028 11.4592C9.06028 10.8849 9.52579 10.4194 10.1 10.4194C10.1632 10.4194 10.2251 10.4251 10.2852 10.4359M9.06028 11.4592L5.57949 11.4591M5.57949 11.4591C5.57949 12.0333 5.11398 12.4988 4.53974 12.4988C3.96551 12.4988 3.5 12.0333 3.5 11.4591C3.5 10.8849 3.96551 10.4194 4.53974 10.4194M5.57949 11.4591C5.57949 10.8849 5.11398 10.4194 4.53974 10.4194M10.2852 10.4359L11.2523 5.56003M10.4374 4.72883C10.5135 5.14579 10.8382 5.47596 11.2523 5.56003C11.3195 5.57367 11.389 5.58083 11.4603 5.58083C12.0345 5.58083 12.5 5.11532 12.5 4.54109C12.5 3.96685 12.0345 3.50134 11.4603 3.50134C10.886 3.50134 10.4205 3.96685 10.4205 4.54109C10.4205 4.60519 10.4263 4.66793 10.4374 4.72883ZM5.55753 5.70877L10.4374 4.72883" stroke="currentColor" strokeWidth="0.675" />
  </svg>
);

const MinimizeIcon = ({ minimized = false }: { minimized?: boolean }) => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d={minimized ? 'M6 2v8M2 6h8' : 'M2 6h8'} stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const CloseIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M2 2l8 8M10 2L2 10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const PanelIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M5.55753 5.70877C5.45903 5.23699 5.04077 4.8826 4.53974 4.8826C3.96551 4.8826 3.5 5.34811 3.5 5.92235C3.5 6.49658 3.96551 6.96209 4.53974 6.96209C5.11398 6.96209 5.57949 6.49658 5.57949 5.92235C5.57949 5.84914 5.57192 5.7777 5.55753 5.70877ZM4.53974 6.96209V10.4194M9.06028 11.4592C9.06028 12.0334 9.52579 12.4989 10.1 12.4989C10.6743 12.4989 11.1398 12.0334 11.1398 11.4592C11.1398 10.9481 10.7711 10.5232 10.2852 10.4359M9.06028 11.4592C9.06028 10.8849 9.52579 10.4194 10.1 10.4194C10.1632 10.4194 10.2251 10.4251 10.2852 10.4359M9.06028 11.4592L5.57949 11.4591M5.57949 11.4591C5.57949 12.0333 5.11398 12.4988 4.53974 12.4988C3.96551 12.4988 3.5 12.0333 3.5 11.4591C3.5 10.8849 3.96551 10.4194 4.53974 10.4194M5.57949 11.4591C5.57949 10.8849 5.11398 10.4194 4.53974 10.4194M10.2852 10.4359L11.2523 5.56003M10.4374 4.72883C10.5135 5.14579 10.8382 5.47596 11.2523 5.56003C11.3195 5.57367 11.389 5.58083 11.4603 5.58083C12.0345 5.58083 12.5 5.11532 12.5 4.54109C12.5 3.96685 12.0345 3.50134 11.4603 3.50134C10.886 3.50134 10.4205 3.96685 10.4205 4.54109C10.4205 4.60519 10.4263 4.66793 10.4374 4.72883ZM5.55753 5.70877L10.4374 4.72883" stroke="currentColor" strokeWidth="0.675" />
  </svg>
);

const getNextGeoFeatureName = (tool: Tool, existingFeatures: GeoFeature[]) => {
  const isPoint = tool === 'target';
  const prefix = isPoint ? 'Point_' : 'Area of Interest_';
  const targetKind = isPoint ? 'point' : 'polygon';

  let maxNum = 0;

  existingFeatures.forEach((gf) => {
    const isMatchingKind = gf.kind === targetKind;
    const isMatchingPrefix = isPoint
      ? gf.name?.startsWith('Point_')
      : (gf.name?.startsWith('Area of Interest_') || gf.name?.startsWith('Polygon_') || gf.name?.startsWith('Area of Interest'));

    if (isMatchingKind || isMatchingPrefix) {
      const match = gf.name?.match(/(\d+)$/);
      if (match) {
        const num = parseInt(match[1], 10);
        if (!isNaN(num) && num > maxNum) {
          maxNum = num;
        }
      }
    }
  });

  const nextNum = maxNum + 1;
  return `${prefix}${String(nextNum).padStart(2, '0')}`;
};

const TOOLS: { key: Tool; label: string; Icon: React.FC }[] = [
  { key: 'target', label: 'Point', Icon: PointTabIcon },
  { key: 'polygon', label: 'Polygon', Icon: PolygonTabIcon },
];

const GeoFeaturePanel: React.FC = () => {
  const {
    geoFeaturePanelOpen, editingFeatureId, geoFeatures,
    closeGeoFeature, addGeoFeature, updateGeoFeature, removeGeoFeature,
    fetchGeoFeatures, leftPanelCollapsed, invalidateAccessWindows,
  } = useMDCockpit();
  const dragStyle = {
    position: 'absolute' as const,
    top: 60,
    left: leftPanelCollapsed ? 12 : 236,
  };
  const cesium = useCesium();
  const { mission_id, scenario_id } = useParams<{ mission_id: string; scenario_id: string }>();

  const [activeTool, setActiveTool] = useState<Tool>('target');
  const [minimized, setMinimized] = useState(false);
  const [name, setName] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    if (geoFeaturePanelOpen) {
      setMinimized(false);
    }
  }, [geoFeaturePanelOpen]);

  const [pointCoords, setPointCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [polygonVertices, setPolygonVertices] = useState<{ lat: string; lng: string }[]>([{ lat: '', lng: '' }]);

  // Lifted target coordinate metadata
  const [coordSystem, setCoordSystem] = useState('geodetic');
  const [altDatum, setAltDatum] = useState<'MSL' | 'WGS84'>('MSL');
  const [altUnit, setAltUnit] = useState<'km' | 'm'>('km');
  const [altValue, setAltValue] = useState('');
  const [xVal, setXVal] = useState('');
  const [yVal, setYVal] = useState('');
  const [zVal, setZVal] = useState('');
  const [loading, setLoading] = useState(false);
  const prevParsedVerticesKeyRef = useRef<string>('');

  const editingFeature = editingFeatureId
    ? geoFeatures.find((gf) => gf.id === editingFeatureId) ?? null
    : null;

  const resetState = (tool: Tool = 'target') => {
    setName(getNextGeoFeatureName(tool, geoFeatures));
    setPointCoords(null);
    setPolygonVertices([{ lat: '', lng: '' }]);
    setIsDrawing(false);
    setActiveTool(tool);
    setCoordSystem('geodetic');
    setAltDatum('MSL');
    setAltUnit('km');
    setAltValue('');
    setXVal('');
    setYVal('');
    setZVal('');
  };

  const loadEditingFeature = (feature: GeoFeature) => {
    setName(feature.name);
    if (feature.kind === 'point') {
      setActiveTool('target');
      setCoordSystem(feature.coordType ?? 'geodetic');
      setAltDatum(feature.altDatum ?? 'MSL');
      setAltUnit(feature.altUnit ?? 'km');
      setAltValue(feature.altitude != null ? String(feature.altitude) : '');
      setXVal(feature.x != null ? String(feature.x) : '');
      setYVal(feature.y != null ? String(feature.y) : '');
      setZVal(feature.z != null ? String(feature.z) : '');
      if (feature.lat != null && feature.lng != null) {
        const isGeocentric = (feature.coordType?.toLowerCase() === 'geocentric');
        const geodeticLat = isGeocentric ? geocentricToGeodetic(feature.lat) : feature.lat;
        setPointCoords({ lat: geodeticLat, lng: feature.lng });
        cesium.updateDraftPoint(geodeticLat, feature.lng);
      } else if (feature.x != null && feature.y != null && feature.z != null) {
        const { lat, lon, altKm } = ecefToGeodetic(feature.x, feature.y, feature.z);
        setPointCoords({ lat: lat, lng: lon });
        cesium.updateDraftPoint(lat, lon);
        const safeAltKm = Math.max(0, altKm);
        setAltValue(feature.altUnit === 'm' ? String(safeAltKm * 1000) : String(safeAltKm));
      }
    } else if (feature.kind === 'polygon') {
      setActiveTool('polygon');
      const coords = feature.coordinates ?? [];
      setPolygonVertices(coords.map((c) => ({ lat: String(c.lat), lng: String(c.lng) })));
      if (coords.length >= 3) {
        cesium.removeGeoFeature(feature.id);
        cesium.loadPolygonForEdit(coords, (vertices) => {
          setPolygonVertices(vertices.map((v) => ({ lat: String(v.lat.toFixed(4)), lng: String(v.lng.toFixed(4)) })));
        });
      }
    }
  };

  useEffect(() => {
    if (geoFeaturePanelOpen && !editingFeatureId) {
      cesium.stopDraw();
      resetState('target');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoFeaturePanelOpen, editingFeatureId]);



  useEffect(() => {
    if (!geoFeaturePanelOpen || minimized || editingFeatureId) return;
    if (activeTool === 'target') {
      handlePickOnMap();
      return;
    }
    handleDrawOnMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geoFeaturePanelOpen, activeTool, minimized, editingFeatureId]);

  useEffect(() => {
    if (!editingFeature) return;
    loadEditingFeature(editingFeature);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingFeatureId]);

  if (!geoFeaturePanelOpen) return null;

  const isGeometryValid = (): boolean => {
    if (activeTool === 'target') {
      if (coordSystem === 'cartesian') {
        return xVal !== '' && yVal !== '' && zVal !== '';
      }
      return pointCoords != null && !isNaN(pointCoords.lat) && !isNaN(pointCoords.lng) && altValue.trim() !== '';
    }
    if (activeTool === 'polygon') {
      const pts = polygonVertices
        .map((v) => ({ lat: parseFloat(v.lat), lng: parseFloat(v.lng) }))
        .filter((v) => !isNaN(v.lat) && !isNaN(v.lng));
      const uniquePts: typeof pts = [];
      for (const pt of pts) {
        if (uniquePts.length === 0) {
          uniquePts.push(pt);
        } else {
          const prev = uniquePts[uniquePts.length - 1];
          if (Math.abs(pt.lat - prev.lat) > 1e-7 || Math.abs(pt.lng - prev.lng) > 1e-7) {
            uniquePts.push(pt);
          }
        }
      }
      if (uniquePts.length > 1) {
        const first = uniquePts[0];
        const last = uniquePts[uniquePts.length - 1];
        if (Math.abs(first.lat - last.lat) < 1e-7 && Math.abs(first.lng - last.lng) < 1e-7) {
          uniquePts.pop();
        }
      }
      return uniquePts.length >= 3;
    }
    return false;
  };

  const isNameValid = (): boolean => {
    return name.trim().length > 0;
  };

  const handleClose = () => {
    cesium.stopDraw();
    if (editingFeature?.kind === 'polygon') {
      cesium.commitGeoFeature(editingFeature);
    }
    closeGeoFeature();
    resetState();
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      showToastMessage('info', 'Geofeature name is required');
      return;
    }

    if (activeTool === 'target') {
      if (coordSystem === 'cartesian') {
        if (xVal === '' || yVal === '' || zVal === '') {
          showToastMessage('info', 'All position coordinates (x, y, z) are required');
          return;
        }
      } else {
        if (pointCoords == null || isNaN(pointCoords.lat) || isNaN(pointCoords.lng)) {
          showToastMessage('info', 'Please pick a point on the globe');
          return;
        }
        if (altValue.trim() === '' || isNaN(parseFloat(altValue))) {
          showToastMessage('info', 'Altitude is required for Geodetic/Geocentric coordinates');
          return;
        }
        const altNum = parseFloat(altValue);
        const maxAlt = altUnit === 'km' ? 10 : 10000;
        if (altNum < 0 || altNum > maxAlt) {
          showToastMessage('info', `Altitude must be between 0 and ${maxAlt} ${altUnit}`);
          return;
        }
      }
    } else if (activeTool === 'polygon') {
      const pts = polygonVertices
        .map((v) => ({ lat: parseFloat(v.lat), lng: parseFloat(v.lng) }))
        .filter((v) => !isNaN(v.lat) && !isNaN(v.lng));
      const uniquePts: typeof pts = [];
      for (const pt of pts) {
        if (uniquePts.length === 0) {
          uniquePts.push(pt);
        } else {
          const prev = uniquePts[uniquePts.length - 1];
          if (Math.abs(pt.lat - prev.lat) > 1e-7 || Math.abs(pt.lng - prev.lng) > 1e-7) {
            uniquePts.push(pt);
          }
        }
      }
      if (uniquePts.length > 1) {
        const first = uniquePts[0];
        const last = uniquePts[uniquePts.length - 1];
        if (Math.abs(first.lat - last.lat) < 1e-7 && Math.abs(first.lng - last.lng) < 1e-7) {
          uniquePts.pop();
        }
      }
      if (uniquePts.length < 3) {
        showToastMessage('info', 'Polygon must have at least 3 unique non-overlapping vertices');
        return;
      }
    }

    let aoiEntry: any;

    if (activeTool === 'polygon') {
      const polygon = polygonVertices
        .map((v) => ({ lat: parseFloat(v.lat), lng: parseFloat(v.lng) }))
        .filter((v) => !isNaN(v.lat) && !isNaN(v.lng));
      const uniquePts: typeof polygon = [];
      for (const pt of polygon) {
        if (uniquePts.length === 0) {
          uniquePts.push(pt);
        } else {
          const prev = uniquePts[uniquePts.length - 1];
          if (Math.abs(pt.lat - prev.lat) > 1e-7 || Math.abs(pt.lng - prev.lng) > 1e-7) {
            uniquePts.push(pt);
          }
        }
      }
      if (uniquePts.length > 1) {
        const first = uniquePts[0];
        const last = uniquePts[uniquePts.length - 1];
        if (Math.abs(first.lat - last.lat) < 1e-7 && Math.abs(first.lng - last.lng) < 1e-7) {
          uniquePts.pop();
        }
      }
      const ring = [...uniquePts, uniquePts[0]].map(v => [v.lng, v.lat]);
      aoiEntry = {
        name: name || 'Area of Interest',
        type: 'Geofence',
        polygon: ring,
        description: '',
      };
    } else {
      const altNum = parseFloat(altValue) || 0;
      const altKm = altUnit === 'm' ? altNum / 1000 : altNum;
      aoiEntry = {
        name: name || 'Point',
        type: 'Target Track',
        description: '',
        altitudeReference: altDatum,
        coordinateType: coordSystem === 'cartesian' ? 'Cartesian'
          : coordSystem === 'geocentric' ? 'Geocentric' : 'Geodetic',
        unit: 'km',
        ...(coordSystem === 'cartesian'
          ? {
            cartesianCoordinates: [parseFloat(xVal) || 0, parseFloat(yVal) || 0, parseFloat(zVal) || 0],
            coordinates: {
              altitude: altKm
            }
          }
          : {
            coordinates: {
              lat: coordSystem === 'geocentric' ? geodeticToGeocentric(pointCoords!.lat) : pointCoords!.lat,
              long: pointCoords!.lng,
              altitude: altKm,
              minElevationAngle: 0
            }
          }
        ),
      };
    }

    const body = {
      areaOfInterests: [aoiEntry],
      name: name || 'AOI',
      type: 'Single',
    };

    // Optimistically update Cesium/state then sync from API
    const featureId = editingFeature?.id ?? `gf-${Date.now()}`;
    const feature: GeoFeature = {
      id: featureId,
      name: name || `New ${activeTool}`,
      kind: activeTool === 'target' ? 'point' : 'polygon',
      type: activeTool === 'target' ? 'Target Track' : 'Geofence',
      active: true,
      createdAt: editingFeature?.createdAt ?? new Date().toISOString(),
      ...(activeTool === 'target' && pointCoords ? { lat: pointCoords.lat, lng: pointCoords.lng } : {}),
      ...(activeTool === 'polygon' ? {
        coordinates: polygonVertices
          .map((v) => ({ lat: parseFloat(v.lat), lng: parseFloat(v.lng) }))
          .filter((v) => !isNaN(v.lat) && !isNaN(v.lng)),
      } : {}),
    };

    if (editingFeature) {
      if (editingFeature.kind !== 'polygon') cesium.removeGeoFeature(editingFeature.id);
      cesium.commitGeoFeature(feature);
      cesium.stopDraw();
      updateGeoFeature(feature);
    } else {
      cesium.commitGeoFeature(feature);
      cesium.stopDraw();
      addGeoFeature(feature);
    }

    let isGfNameOnly = false;
    if (editingFeature) {
      if (editingFeature.kind === 'point' && pointCoords) {
        isGfNameOnly = editingFeature.lat === pointCoords.lat && editingFeature.lng === pointCoords.lng;
      } else if (editingFeature.kind === 'polygon') {
        const parsedCoords = polygonVertices
          .map((v) => ({ lat: parseFloat(v.lat), lng: parseFloat(v.lng) }))
          .filter((v) => !isNaN(v.lat) && !isNaN(v.lng));
        const originalCoords = editingFeature.coordinates ?? [];
        if (parsedCoords.length === originalCoords.length) {
          isGfNameOnly = parsedCoords.every((coord, idx) => {
            const orig = originalCoords[idx];
            return orig && orig.lat === coord.lat && orig.lng === coord.lng;
          });
        }
      }
    }

    setLoading(true);
    try {
      if (mission_id && scenario_id) {
        let res;
        if (editingFeature) {
          res = await updateGeoFeatureApi(mission_id, scenario_id, editingFeature.id, aoiEntry);
        } else {
          res = await createGeoFeature(mission_id, scenario_id, body);
        }
        invalidateAccessWindows();
        await fetchGeoFeatures(mission_id, scenario_id);

        const successMsg = res?.data?.message || res?.message || (editingFeature ? 'GeoFeature updated successfully' : 'GeoFeature created successfully');
        showToastMessage('success', successMsg);

        closeGeoFeature();
        resetState();
      }
    } catch (e: any) {
      console.error('[GeoFeature] Save failed', e);

      // Revert optimistic updates
      if (editingFeature) {
        cesium.commitGeoFeature(editingFeature);
        updateGeoFeature(editingFeature);
        loadEditingFeature(editingFeature);
      } else {
        cesium.removeGeoFeature(featureId);
        removeGeoFeature(featureId);
      }

      const errorMsg = e?.response?.data?.message || e?.response?.data?.Message || e?.data?.message || e?.message || (editingFeature ? 'Failed to update GeoFeature' : 'Failed to create GeoFeature');
      showToastMessage('error', errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleAltValueChange = (v: string) => {
    setAltValue(v);
    const altNum = parseFloat(v) || 0;
    const altKm = altUnit === 'm' ? altNum / 1000 : altNum;
    if (pointCoords && !isNaN(pointCoords.lat) && !isNaN(pointCoords.lng)) {
      const { x, y, z } = geodeticToEcef(pointCoords.lat, pointCoords.lng, altKm);
      setXVal(x.toFixed(4));
      setYVal(y.toFixed(4));
      setZVal(z.toFixed(4));
    }
  };

  const handleAltUnitChange = (unit: 'km' | 'm') => {
    setAltUnit(unit);
  };

  const syncGeodeticFromCartesian = (xStr: string, yStr: string, zStr: string) => {
    const x = parseFloat(xStr);
    const y = parseFloat(yStr);
    const z = parseFloat(zStr);
    if (!isNaN(x) && !isNaN(y) && !isNaN(z)) {
      const { lat, lon, altKm } = ecefToGeodetic(x, y, z);
      setPointCoords({ lat, lng: lon });
      cesium.updateDraftPoint(lat, lon);
      const safeAltKm = Math.max(0, altKm);
      setAltValue(altUnit === 'm' ? (safeAltKm * 1000).toFixed(1) : safeAltKm.toFixed(4));
    }
  };

  const handleXChange = (v: string) => {
    setXVal(v);
    syncGeodeticFromCartesian(v, yVal, zVal);
  };

  const handleYChange = (v: string) => {
    setYVal(v);
    syncGeodeticFromCartesian(xVal, v, zVal);
  };

  const handleZChange = (v: string) => {
    setZVal(v);
    syncGeodeticFromCartesian(xVal, yVal, v);
  };

  // Point handlers
  const handlePickOnMap = () => {
    setIsDrawing(true);
    cesium.startPointDraw((lat, lng) => {
      setPointCoords({ lat, lng });
      cesium.updateDraftPoint(lat, lng);
      // Synchronize Cartesian
      const altNum = parseFloat(altValue) || 0;
      const altKm = altUnit === 'm' ? altNum / 1000 : altNum;
      const { x, y, z } = geodeticToEcef(lat, lng, altKm);
      setXVal(x.toFixed(4));
      setYVal(y.toFixed(4));
      setZVal(z.toFixed(4));
      setIsDrawing(false);
    });
  };

  const handlePointCoordsChange = (lat: number, lng: number) => {
    setPointCoords({ lat, lng });
    cesium.updateDraftPoint(lat, lng);
    // Synchronize Cartesian
    const altNum = parseFloat(altValue) || 0;
    const altKm = altUnit === 'm' ? altNum / 1000 : altNum;
    if (!isNaN(lat) && !isNaN(lng)) {
      const { x, y, z } = geodeticToEcef(lat, lng, altKm);
      setXVal(x.toFixed(4));
      setYVal(y.toFixed(4));
      setZVal(z.toFixed(4));
    }
  };

  // Polygon handlers
  const handleDrawOnMap = () => {
    setIsDrawing(true);
    cesium.startPolygonDraw(
      (vertices) => {
        setPolygonVertices(vertices.map((v) => ({ lat: String(v.lat.toFixed(4)), lng: String(v.lng.toFixed(4)) })));
      },
      (vertices) => {
        setPolygonVertices(vertices.map((v) => ({ lat: String(v.lat.toFixed(4)), lng: String(v.lng.toFixed(4)) })));
        setIsDrawing(false);
        cesium.startPolygonVertexEdit((vs) => {
          setPolygonVertices(vs.map((v) => ({ lat: String(v.lat.toFixed(4)), lng: String(v.lng.toFixed(4)) })));
        });
      },
    );
  };

  const handlePolygonVerticesChange = (vertices: { lat: string; lng: string }[]) => {
    setPolygonVertices(vertices);
    const parsed = vertices
      .map((v) => ({ lat: parseFloat(v.lat), lng: parseFloat(v.lng) }))
      .filter((v) => !isNaN(v.lat) && !isNaN(v.lng) && isFinite(v.lat) && isFinite(v.lng));
    const key = JSON.stringify(parsed);
    if (key !== prevParsedVerticesKeyRef.current) {
      prevParsedVerticesKeyRef.current = key;
      cesium.updateDraftPolygon(parsed);
    }
  };

  const isEditing = !!editingFeature;

  const bannerMessages: Record<Tool, string> = {
    target: 'Click globe to place point',
    polygon: 'Left-click to add vertices · Right-click to finish',
  };

  return (
    <div
      className={styles.panel}
      style={{ ...dragStyle }}
      onPointerDown={e => e.stopPropagation()}
      onPointerMove={e => e.stopPropagation()}
      onPointerUp={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
      onMouseMove={e => e.stopPropagation()}
      onMouseUp={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
      onContextMenu={e => e.stopPropagation()}
    >
      <div className={styles.header}>
        <span className={styles.headerLeft}>
          <span className={styles.headerIcon}><PanelIcon /></span>
          {isEditing ? 'EDIT FEATURE' : 'GEOFEATURE'}
        </span>
        <span className={styles.headerRight}>
          <button className={styles.headerBtn} type="button" title={minimized ? 'Restore' : 'Minimize'} onClick={() => setMinimized(m => !m)} onPointerDown={e => e.stopPropagation()}>
            <MinimizeIcon minimized={minimized} />
          </button>
          <button className={styles.headerBtn} type="button" title="Close" onClick={handleClose} onPointerDown={e => e.stopPropagation()}>
            <CloseIcon />
          </button>
        </span>
      </div>

      {isDrawing && !minimized && (
        <div className={styles.drawBanner}>
          <span className={styles.bannerPulse} />
          <span>{bannerMessages[activeTool]}</span>
        </div>
      )}

      {!minimized && (
        <>
          {!isEditing && (
            <div className={styles.tabs}>
              {TOOLS.map(t => (
                <button
                  key={t.key}
                  className={`${styles.tab} ${activeTool === t.key ? styles.tabActive : ''}`}
                  type="button"
                  onClick={() => {
                    setActiveTool(t.key);
                    if (!editingFeatureId) {
                      setName(getNextGeoFeatureName(t.key, geoFeatures));
                    }
                    cesium.stopDraw();
                    setIsDrawing(false);
                  }}
                >
                  <t.Icon />
                  {t.label}
                </button>
              ))}
            </div>
          )}

          <div className={styles.body}>
            {activeTool === 'target' && (
              <TargetBody
                name={name} onNameChange={setName}
                lat={pointCoords?.lat ?? null} lng={pointCoords?.lng ?? null}
                onCoordsChange={handlePointCoordsChange}
                isDrawing={isDrawing}
                coordSystem={coordSystem} onCoordSystemChange={setCoordSystem}
                altDatum={altDatum} onAltDatumChange={setAltDatum}
                altUnit={altUnit} onAltUnitChange={handleAltUnitChange}
                altValue={altValue} onAltValueChange={handleAltValueChange}
                xVal={xVal} onXChange={handleXChange}
                yVal={yVal} onYChange={handleYChange}
                zVal={zVal} onZChange={handleZChange}
              />
            )}
            {activeTool === 'polygon' && (
              <PolygonBody
                name={name} onNameChange={setName}
                vertices={polygonVertices} onVerticesChange={handlePolygonVerticesChange}
                isDrawing={isDrawing}
              />
            )}
          </div>

          <div className={styles.footer}>
            <button className={styles.cancelBtn} type="button" disabled={loading} onClick={handleClose}>Close</button>
            <button
              className={styles.createBtn}
              type="button"
              disabled={loading}
              onClick={handleCreate}
            >
              {loading ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'Update' : 'Create')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default GeoFeaturePanel;
